import { Overlay, OverlayConfig } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';

import {
  Component,
  ElementRef,
  effect,
  Injector,
  inject,
  input,
  model,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BbitI18n, BbitUtils, IBbitI18n, IBbitSelectDataRow } from '@bbitgmbh/bbit.utils';
import { I18nPipe } from '@bbitgmbh/website.sdk';
import { ComboboxOverlayComponent } from './combobox-overlay/combobox-overlay.component';
import { ComboboxOverlayRef } from './combobox-overlay/combobox-overlay.ref';

@Component({
  selector: 'app-combobox',
  imports: [ReactiveFormsModule, FormsModule, I18nPipe],
  templateUrl: './combobox.component.html',
  styleUrl: './combobox.component.scss',
})
export class ComboboxComponent {
  public id = signal(`combobox-${BbitUtils.makeId({ leadingHashtag: false })}`);
  public values = input<IBbitSelectDataRow[] | null | undefined>();
  private _filteredValues = signal<IBbitSelectDataRow[]>([]);
  public name = input<string>();
  public ngModel = input<string>();
  public placeholder = input<IBbitI18n>();
  public noRowsText = input<IBbitI18n>();
  public allowNewEntry = input<boolean>(false);
  public newEntryText = input<IBbitI18n>();
  public label = input<string>();
  public input = viewChild<ElementRef>('input');
  public filterOnly = input<boolean>(false);
  private _overlay = inject(Overlay);
  private _overlayRefInstance?: ComboboxOverlayRef;
  public filter = model('');
  public selected = output<IBbitSelectDataRow>();
  public createNewEntry = output<void>();
  // biome-ignore lint/correctness/noUnusedPrivateClassMembers: required
  private _filterChange = effect(() => {
    if (this.filter()) {
      this._filteredValues.set(
        this.values()?.filter((value) =>
          BbitI18n.t(value.displayValue).toLowerCase().includes(this.filter().toLowerCase()),
        ) || [],
      );
      return;
    }
    this._filteredValues.set(this.values() || []);
  });

  public showOverlay(): void {
    if (this._overlayRefInstance) {
      return;
    }
    const overlayConfig = new OverlayConfig({
      hasBackdrop: true,
      backdropClass: '',
      width: this.input()?.nativeElement.clientWidth,
      maxHeight: '250px',
      scrollStrategy: this._overlay.scrollStrategies.block(),
      positionStrategy: this._overlay
        .position()
        .flexibleConnectedTo(this.input()?.nativeElement)
        .withPositions([
          // Define your preferred positions in order of priority
          {
            originX: 'start',
            originY: 'bottom',
            overlayX: 'start',
            overlayY: 'top',
          },
          {
            originX: 'start',
            originY: 'top',
            overlayX: 'start',
            overlayY: 'bottom',
          },
        ])
        .withPush(false),
    });
    const overlayRef = this._overlay.create(overlayConfig);
    this._overlayRefInstance = new ComboboxOverlayRef(overlayRef);
    this._overlayRefInstance.setData({
      values: this._filteredValues,
      allowNewEntry: this.allowNewEntry,
      newEntryText: this.newEntryText,
      noRowsText: this.noRowsText,
    });
    const injector = Injector.create({
      providers: [{ provide: ComboboxOverlayRef, useValue: this._overlayRefInstance }],
    });
    const containerPortal = new ComponentPortal(ComboboxOverlayComponent, null, injector);
    overlayRef.attach(containerPortal);
    overlayRef.backdropClick().subscribe((): void => {
      this._overlayRefInstance?.close();
    });

    this._overlayRefInstance.observeClose().subscribe((data) => {
      if (data?.selected) {
        this.selected.emit(data.selected);
      }
      if (data?.createNewEntry) {
        this.createNewEntry.emit();
      }
      this.filter.set('');
      this._overlayRefInstance = undefined;
    });
  }

  public onKeyDown(event: KeyboardEvent): void {
    if (this._overlayRefInstance) {
      if (event.key === 'Escape') {
        this.hideOverlay();
      }
      if (event.key === 'Enter') {
        this._overlayRefInstance.selectCurrent();
      }
      if (event.key === 'ArrowDown') {
        this._overlayRefInstance.selectNext();
      }
      if (event.key === 'ArrowUp') {
        this._overlayRefInstance.selectPrevious();
      }
      return;
    }
    this.showOverlay();
  }

  public hideOverlay(): void {
    setTimeout(() => {
      if (this._overlayRefInstance) {
        this._overlayRefInstance.close();
      }
    }, 250);
  }
}
